import { CreateComponentPublicInstance } from 'vue';
import dayjs from 'dayjs';
import { GreaterThanOrEqual, Null, Or } from '@llws/dynamic-router-utils';
import { premiumReportingClientCities } from '@/store/typed/typed-modules';
import { TRangeOption } from '@/components/_global/RsFormDateRangePicker.vue';
import i18n from '@/i18n';
import sisense, { getPremiumReportingCities } from '../services/sisense';
import { TSelectOptionWithStringValue } from '@/components/RsSidebarFilters.vue';
import { adminRequest } from '../request-configs/admin-request';

export type TPRAmenity = {
  id: number,
  standardAmenity: string,
  key: string,
};

const monthKeys = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const getMonthYearLabel = (monthsAgo: number): string => {
  const date = dayjs().subtract(monthsAgo, 'month');
  return `${i18n.t(`calendar.months.${monthKeys[date.month()]}`)}, ${date.format('YYYY')}`;
};

export const prCurrentDateOptions: TRangeOption[] = [
  {
    label: i18n.t('marketInsights.filters.dateOptions.currentMonth') as string,
    minDate: dayjs().startOf('month').toDate(),
    maxDate: dayjs().endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(1),
    minDate: dayjs().subtract(1, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(1, 'month').endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(2),
    minDate: dayjs().subtract(2, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(2, 'month').endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(3),
    minDate: dayjs().subtract(3, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(3, 'month').endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(4),
    minDate: dayjs().subtract(4, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(4, 'month').endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(5),
    minDate: dayjs().subtract(5, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(5, 'month').endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(6),
    minDate: dayjs().subtract(6, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(6, 'month').endOf('month').toDate(),
  },
  {
    label: getMonthYearLabel(7),
    minDate: dayjs().subtract(7, 'month').startOf('month').toDate(),
    maxDate: dayjs().subtract(7, 'month').endOf('month').toDate(),
  },
];

export const prHistoricalDateOptions: TRangeOption[] = [
  {
    label: i18n.t('marketInsights.filters.dateOptions.last24Months') as string,
    minDate: dayjs().subtract(24, 'month').startOf('month').toDate(),
    maxDate: dayjs().endOf('month').toDate(),
  },
  {
    label: i18n.t('marketInsights.filters.dateOptions.last18Months') as string,
    minDate: dayjs().subtract(18, 'month').startOf('month').toDate(),
    maxDate: dayjs().endOf('month').toDate(),
  },
  {
    label: i18n.t('marketInsights.filters.dateOptions.last12Months') as string,
    minDate: dayjs().subtract(12, 'month').startOf('month').toDate(),
    maxDate: dayjs().endOf('month').toDate(),
  },
  {
    label: i18n.t('marketInsights.filters.dateOptions.last6Months') as string,
    minDate: dayjs().subtract(6, 'month').startOf('month').toDate(),
    maxDate: dayjs().endOf('month').toDate(),
  },
];

export const getPRCities = async (
  vm: CreateComponentPublicInstance,
  includeFSADefaults?: boolean,
  suppressNoCitySubscriptionError?: boolean,
) => {
  const now = dayjs().format('YYYY-MM-DD');

  if (!premiumReportingClientCities.getters.list().length) {
    await premiumReportingClientCities.actions.get({
      filters: {
        where: [
          {
            field: 'clientId',
            value: `${vm?.$rsGlobals.$currentClient.id}`,
            notOperator: false,
            operator: '',
            condition: 'and',
          },
          {
            field: 's.startDate',
            value: now,
            operator: 'lte',
          },
          {
            condition: 'and',
            whereCommands: [
              Or(GreaterThanOrEqual('s.expiryDate', now)),
              Or(Null('s.expiryDate')),
            ],
          },
        ],
        limit: 9999,
      },
    });
  }

  const cities = premiumReportingClientCities.getters.listNormalized();

  if (!cities.length) {
    if (!suppressNoCitySubscriptionError) {
      vm.$eventBus.$emit('show-error-toast', {
        message: 'No active Market Insights City subscriptions! Please contact support for more info.',
      });
      throw new Error('No active city subscriptions for Market Insights!');
    }

    return { cities: [], defaultFSAs: [] };
  }

  if (!includeFSADefaults) {
    return { cities, defaultFSAs: [] };
  }

  const [ primaryCity ] = cities;

  const defaultFSAs = primaryCity ? [
    ...primaryCity.cityFSADefault ? [ primaryCity.cityFSADefault ] : [],
    ...primaryCity.secondaryCityFSADefault ? [ primaryCity.secondaryCityFSADefault ] : [],
  ] : [];

  if (!defaultFSAs.length) {
    const fsas = await sisense.getPremiumReportingFSAs([ cities[0]?.cityId ]);
    return { cities, defaultFSAs: (fsas ?? [])?.slice(0, 4).map((f) => f.fsa) };
  }

  return { cities, defaultFSAs };
};

export const getPRCitiesWithSisenseIds = async (
  vm: CreateComponentPublicInstance,
  defaultFSAs?: boolean,
  suppressNoCitySubscriptionError?: boolean,
) => {
  const liftRes = await getPRCities(vm, defaultFSAs, suppressNoCitySubscriptionError);
  const sisenseRes = await getPremiumReportingCities(liftRes.cities.map((c) => c.cityId), true);

  return {
    subscribedCities: liftRes.cities,
    cities: liftRes.cities.map((c) => {
      const sisenseCity = sisenseRes?.find((s) => `${s.subText}` === `${c.cityId}`);
      return sisenseCity ?? { text: `Market City Not Found (${c.city?.cityName})`, value: '' };
    }).sort((a, b) => (a.text > b.text ? 1 : -1)),
    defaultFSAs: liftRes.defaultFSAs,
  };
};

export const getPRAmenities = async () => {
  const response = await adminRequest.post<TPRAmenity[]>('/client-api/premium-reporting-amenities/_search', {});
  return response.data ?? [];
};

export type TPRStaticOptionKeys =
  'bedrooms'
  | 'propertyTypes'
  | 'utilities'
  | 'parkingTypes'
  | 'petFriendlyTypes';

export const prSelectOptions: Record<TPRStaticOptionKeys, TSelectOptionWithStringValue[]> = {
  bedrooms: [
    { text: '0', value: '0 Bed' },
    { text: '1', value: '1 Bed' },
    { text: '2', value: '2 Bed' },
    { text: '3', value: '3 Bed' },
  ],
  propertyTypes: [
    { text: i18n.t('marketInsights.filters.propertyTypes.apartment') as string, value: 'Apartment' },
    { text: i18n.t('marketInsights.filters.propertyTypes.basement') as string, value: 'Basement' },
    { text: i18n.t('marketInsights.filters.propertyTypes.condo') as string, value: 'Condo' },
    { text: i18n.t('marketInsights.filters.propertyTypes.house') as string, value: 'House' },
    { text: i18n.t('marketInsights.filters.propertyTypes.mainFloor') as string, value: 'Main Floor' },
    { text: i18n.t('marketInsights.filters.propertyTypes.multiplex') as string, value: 'Multiplex' },
    { text: i18n.t('marketInsights.filters.propertyTypes.townhouse') as string, value: 'Townhouse' },
  ],
  utilities: [
    { text: i18n.t('marketInsights.filters.utilities.electricity') as string, value: 'utility_electricity' },
    { text: i18n.t('marketInsights.filters.utilities.heat') as string, value: 'utility_heat' },
    { text: i18n.t('marketInsights.filters.utilities.water') as string, value: 'utility_water' },
  ],
  parkingTypes: [
    { text: i18n.t('marketInsights.filters.parkingAvailable.available') as string, value: '1' },
    { text: i18n.t('marketInsights.filters.parkingAvailable.unavailable') as string, value: '0' },
  ],
  petFriendlyTypes: [
    { text: i18n.t('marketInsights.filters.petFriendlyTypes.allowed') as string, value: '1' },
    { text: i18n.t('marketInsights.filters.petFriendlyTypes.refused') as string, value: '0' },
  ],
};
